import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getTidioPublic } from "../lib/api";

/**
 * TidioLoader — injects Tidio's official `<script>` snippet into the
 * document head once per page load.
 *
 * Behavior:
 *   1. On mount, fetch the saved Tidio project key from
 *      GET /api/site/tidio (admin sets this once via the Tidio tab).
 *   2. If a key exists, inject `<script async src="//code.tidio.co/KEY.js">`
 *      exactly the way Tidio's own install docs require.
 *   3. Hide the widget on admin / checkout / success / cart so it never
 *      blocks conversion flows or duplicates the admin UI.
 *
 * Idempotent — we mark a `data-tidio-injected` attribute on the script
 * and skip re-injection on subsequent renders. The script can co-exist
 * across SPA route changes; Tidio's runtime manages its own widget
 * mount/unmount automatically.
 */
const HIDDEN_PATTERNS = [
  /^\/admin(\/|$)/i,
  /^\/checkout/i,
  /^\/success/i,
  /^\/cart$/i,
];

const SCRIPT_ID = "tidio-chat-script";

function setWidgetVisible(visible) {
  // Tidio exposes `window.tidioChatApi` once loaded. Use its official
  // `display()` method to show/hide on route changes — no DOM hacks.
  try {
    if (window.tidioChatApi) {
      window.tidioChatApi.display(visible);
    }
  } catch { /* ignore */ }
}

export default function TidioLoader() {
  const location = useLocation();
  const onHiddenPath = HIDDEN_PATTERNS.some((re) => re.test(location.pathname));

  // Inject the script exactly once (regardless of route).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getTidioPublic();
        if (cancelled || !cfg?.project_key) return;
        if (document.getElementById(SCRIPT_ID)) return;  // already injected
        const s = document.createElement("script");
        s.id = SCRIPT_ID;
        s.src = `//code.tidio.co/${cfg.project_key}.js`;
        s.async = true;
        s.setAttribute("data-tidio-injected", "1");
        document.head.appendChild(s);
      } catch {
        // Quiet failure — if our backend can't reach Mongo we don't want
        // to spam the customer with errors on a marketing page.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Show/hide widget per-route via Tidio's official API.
  useEffect(() => {
    setWidgetVisible(!onHiddenPath);
  }, [onHiddenPath]);

  return null;
}
